import React, { useCallback, useState, useRef, useEffect, useMemo } from "react";
import { AutoComplete } from "primereact/autocomplete";
import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { ApiCall } from "../services/ApiServices";
import { Debounce } from "../utlis/Debounce";
import LabelField from "./LabelField";
import * as ENDPOINTS from "routes/ApiEndpoints";

interface MultiTableProps {
  label: string;
  group: string;
  modelKey: string;
  column?: string;
  valueColumn?: string;
  concat?: string | null;
  joinTable?: string | null;
  notNullColumn?: string | null;
  customFetch?: boolean;
  separator?: string;
}

export interface FilterProps {
  column: string;
  value: any;
  condition: "where" | "orWhere";
}

interface BaseAutocompleteDropdownProps {
  ref?: React.RefObject<AutoComplete>;
  label?: string;
  labelClassName?: string;
  isMandatory?: boolean;
  id?: string;
  placeholder?: string;
  shouldCreateNew?: boolean;
  isMultiSelect?: boolean;
  microService: string;
  onChange: (value: any) => void;
  onBlur?: () => void;
  value: any;
  showDropdownList?: boolean;
  columnName?: string;
  modelKey: string;
  error?: string;
  concat?: string;
  name?: string;
  valueColumn?: string;
  isGrouped?: boolean;
  multiTables?: MultiTableProps[];
  isDisabled?: boolean;
  customFetch?: boolean;
  separator?: string;
  group?: string;
  notNullColumn?: string;
  className?: string;
  clearString?: boolean;
  ItemclassName?: string;
  isDisabledStatus?: number;
}

interface AutocompleteDropdownPropsWithoutFilter extends BaseAutocompleteDropdownProps {
  filter?: false;
  filterInfo?: never;
}

interface AutocompleteDropdownPropsWithFilter extends BaseAutocompleteDropdownProps {
  filter: true | undefined;
  filterInfo: FilterProps[] | undefined;
}

type AutocompleteDropdownProps =
  | AutocompleteDropdownPropsWithoutFilter
  | AutocompleteDropdownPropsWithFilter;

interface AutoCompleteCompleteMethodParams {
  query: string;
}
const modelKeysWithMinLengthRule = ["BE", "FR", "NL", "city"];

const AutoCompleteDropdown: React.FC<AutocompleteDropdownProps> = ({
  ref,
  label,
  labelClassName,
  isMandatory,
  id,
  placeholder,
  shouldCreateNew = false,
  isMultiSelect = false,
  microService,
  onChange,
  onBlur,
  value,
  showDropdownList = false,
  columnName = "name",
  modelKey,
  error,
  concat = null,
  name,
  valueColumn = 'id',
  isGrouped = false,
  multiTables = [],
  isDisabled = false,
  customFetch = false,
  separator = null,
  filter = false,
  filterInfo = [],
  group = null,
  notNullColumn = null,
  className,
  clearString = false,
  ItemclassName,
  isDisabledStatus
}) => {
  const [filteredItems, setFilteredItems] = useState<string[]>([]);
  const autoCompleteRef = useRef<HTMLDivElement>(null);
  const latestRequestId = useRef(0);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (autoCompleteRef.current && !autoCompleteRef.current.contains(event.target as Node) && !isMultiSelect && clearString) {
        // Clear the input field when clicking outside
        if (typeof value === 'string') {
          onChange("");
        }
      }
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [autoCompleteRef, value, onChange, isMultiSelect, clearString]);

  const defaultColumn = 'name';
  const defaultValueColumn = 'id';
  const defaultConcatTable = null;
  const defaultJoinTable = null;
  const defaultNotNull = null;

  // Ensure default values for column and valueColumn using destructuring
  const processedMultiTables = multiTables.map(({
    label,
    group,
    modelKey,
    column = defaultColumn,
    valueColumn = defaultValueColumn,
    concat = defaultConcatTable,
    joinTable = defaultJoinTable,//Join is not implemented
    notNullColumn = defaultNotNull,
    customFetch = false,
    separator = null
  }) => ({
    label,
    group,
    modelKey,
    column,
    valueColumn,
    concat,
    joinTable,//Join is not implemented
    notNullColumn,
    customFetch,
    separator
  }));
  const [itemSize, setItemSize] = useState<number>(window.innerWidth * (1.979 / 100)); // Initial calculation

  useEffect(() => {
    const handleResize = () => {
      // Calculate item size in pixels based on vw units
      const newSize = window.innerWidth * (1.979 / 100);

      // Only update state if the new size is different
      setItemSize((prevSize) => (prevSize !== newSize ? newSize : prevSize));
    };

    // Use requestAnimationFrame for optimized resize handling
    const resizeListener = () => requestAnimationFrame(handleResize);

    window.addEventListener('resize', resizeListener);

    return () => window.removeEventListener('resize', resizeListener);
  }, []);

  // Clear filtered items when modelKey changes to prevent stale DOM issues
  useEffect(() => {
    setFilteredItems([]);
  }, [modelKey]);

  // Function to fetch items from the API
  const searchItems = async (query: string, currentRequestId: number) => {
    try {
      const response = await ApiCall.service(
        ENDPOINTS.GET_SEARCH_VALUES,
        "POST",
        {
          modelKey: modelKey, value: query, column: columnName, customFetch: customFetch, concat: concat,
          separator: separator, valueColumn: valueColumn, multiTables: processedMultiTables, filter: filter,
          filterInfo: filterInfo, group: group, notNullColumn: notNullColumn
        },
        false,
        microService
      );
      if (response.status === 200 && currentRequestId === latestRequestId.current) {
        const items = response.found;
        setFilteredItems(items);
      } else {
        console.error("Error fetching data:", response?.msg);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  // Debounced search function to limit API calls while typing
  const debouncedSearchItems = useCallback(
    Debounce((query: string) => {
      // Increment requestId before making the API call
      const currentRequestId = ++latestRequestId.current;
      searchItems(query, currentRequestId);
    }, 500),
    [modelKey]
  );

  // Handler for the search event from the AutoComplete component
  const handleSearch = (event: AutoCompleteCompleteMethodParams) => {
    const query = event.query?.trim() ?? "";
    if (query.length >= 3 && modelKeysWithMinLengthRule.includes(modelKey)) {
      debouncedSearchItems(query);
    } else if (!modelKeysWithMinLengthRule.includes(modelKey)) {
      debouncedSearchItems(query);
    }
  };

  // Custom item template for dropdown items
  const itemTemplate = (item: any) => {
    return (
      <div className={`custom-autocomplete-item ${ItemclassName || ''} ${item.status === isDisabledStatus ? 'itemDisabled' : ''}`}>
        {item.label} {/* Adjust this based on the structure of `item` */}
      </div>
    );
  };

  // Ensure the currently-selected value is always part of the suggestion list.
  // PrimeReact's forceSelection clears the field on blur when the displayed value
  // isn't found among the suggestions. That happens whenever the value was set
  // programmatically without a search (edit load, "same as residence", address
  // lookup), leaving the suggestions empty — which made a valid city disappear.
  // Scoped to exactly that case: single-select, non-grouped, forceSelection on
  // (shouldCreateNew=false), with an object value. Grouped/free-text/multi-select
  // usages are left untouched.
  const forceSelectionEnabled = !shouldCreateNew;
  const suggestions = useMemo(() => {
    if (
      !isMultiSelect &&
      !isGrouped &&
      forceSelectionEnabled &&
      value &&
      typeof value === "object" &&
      value.label
    ) {
      const alreadyListed = filteredItems.some(
        (item: any) => item?.label === value.label
      );
      if (!alreadyListed) {
        return [value, ...filteredItems];
      }
    }
    return filteredItems;
  }, [filteredItems, value, isMultiSelect, isGrouped, forceSelectionEnabled]);

  // Only use virtual scroller when there are enough items to benefit from it
  const useVirtualScroller = filteredItems.length > 10;

  return (
    <div className="p-fluid">
      {label && (
        <LabelField
          title={label}
          className={labelClassName}
          isMandatory={isMandatory}
        />
      )}
      <div ref={autoCompleteRef}>
        <AutoComplete
          onBlur={onBlur}
          ref={ref}
          name={name}
          value={value}
          suggestions={suggestions}
          completeMethod={handleSearch}
          onChange={(e) => onChange(e.value)}
          dropdown={showDropdownList}
          multiple={isMultiSelect}
          placeholder={placeholder}
          forceSelection={!shouldCreateNew}
          {...(isGrouped ? { optionGroupLabel: "label", optionGroupChildren: "items" } : {})}
          inputId={id}
          virtualScrollerOptions={useVirtualScroller ? { itemSize } : undefined}
          inputClassName={`autoCompleteInput ${className}`}
          panelStyle={{ maxWidth: autoCompleteRef?.current?.querySelector('input')?.offsetWidth }}
          field="label"
          inputStyle={isDisabled ? { color: '#212529', } : {}}
          disabled={isDisabled}
          itemTemplate={itemTemplate}
        />
      </div>
      <div className="height-20" style={{ marginBottom: "0.5vw" }}>
        {error && <span className="text-danger">{error}</span>}
      </div>
    </div>
  );
};

export default AutoCompleteDropdown;
